import { useEffect, useMemo, useRef, useState } from "react";
import { beginInteraction, endInteraction, setDiagModule } from "@/lib/diag";
import { newIdemKey, idemHeaders } from "@/lib/idem";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/format";
import { printDoc, itemsTable, totalsBlock, pickupBlock, refundBody } from "@/lib/print";
import { fulfillmentBadge } from "@/pages/Pickups";

// Derived server-side from the refunds ledger (refund_status: "full" | "partial" | null) — never a stored UI flag.
const refundBadge = (inv, cls = "") => {
  if (inv?.refund_status === "full") return <Badge className={`${cls} bg-rose-600 text-white hover:bg-rose-600 border-transparent`} data-testid={`refund-badge-${inv.invoice_number}`}>REFUNDED</Badge>;
  if (inv?.refund_status === "partial") return <Badge className={`${cls} bg-amber-500 text-white hover:bg-amber-500 border-transparent`} data-testid={`refund-badge-${inv.invoice_number}`}>PARTIAL REFUND</Badge>;
  return null;
};
import { walletName } from "@/lib/wallet";
import { waNumberFor, waBusinessMessage } from "@/lib/wa";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ManagerPinModal from "@/components/ManagerPinModal";
import DocumentShareDialog from "@/components/documents/DocumentShareDialog";
import { Receipt, Search, Printer, RotateCcw, Eye, ShieldCheck, Share2 } from "lucide-react";

export default function SalesHistory() {
  const { business } = useAuth();
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [detail, setDetail] = useState(null);       // full sale detail (with refunds + refundable)
  const [refundMode, setRefundMode] = useState(false);
  const refundInFlight = useRef(false); const refundIdemKey = useRef(null); const [refundBusy, setRefundBusy] = useState(false);
  const reprintRef = useRef(null);                  // focus recovery target after a receipt window reports afterprint/closed
  const refocusDetail = () => reprintRef.current?.focus();
  const [refundQty, setRefundQty] = useState({});    // key -> qty
  const [disposition, setDisposition] = useState({}); // Fashion: key -> {kind: restock|hold, reason}
  const [refundDelivery, setRefundDelivery] = useState(false);
  const [includeTax, setIncludeTax] = useState(true);
  const [termBlocked, setTermBlocked] = useState(false);
  const TERMINAL_MSG = "Refunds must be processed from a registered POS terminal. Please open this sale on a registered register to continue.";
  const [reason, setReason] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  // B-30 — reusable sharing: {kind:'sale'|'refund', id, label, waMessage, customerEmail, customerPhone} or null.
  const [shareDoc, setShareDoc] = useState(null);
  const shareBase = () => (shareDoc?.kind === "refund" ? "refunds" : "sales");
  const fetchCustomerContact = async (cid) => {
    if (!cid) return { email: "", phone: "" };
    try { const { data } = await api.get(`/customers/${cid}`); return { email: data?.email || "", phone: waNumberFor(data) }; }
    catch { return { email: "", phone: "" }; }
  };
  const openShareSale = async (inv) => {
    const contact = await fetchCustomerContact(inv.customer_id);
    setShareDoc({
      kind: "sale", id: inv.id, customerId: inv.customer_id || null, label: `${inv.is_credit ? "Account Sale" : "Sales Receipt"} ${inv.invoice_number}`, ...contact,
      waMessage: waBusinessMessage({
        business, kind: "receipt", heading: inv.is_credit ? "Account Sale" : "Sales Receipt",
        meta: [`Reference: ${inv.invoice_number}`, `Date: ${fmtDate(inv.created_at)}`],
        greeting: inv.customer_name !== "Walk-in" ? inv.customer_name : "",
        body: inv.is_credit
          ? [`Total: *${money(inv.total)}*`, `On Account: *${money(inv.amount_on_account ?? (inv.total - (inv.amount_paid || 0)))}*`, `Due: ${fmtDate(inv.due_date)}`]
          : [`Total: *${money(inv.total)}*`, `Status: *Paid in Full*`],
        closing: inv.is_credit ? "Please settle the balance above by the due date shown." : "Thank you for your business.",
      }),
    });
  };
  const openShareRefund = async (r) => {
    const contact = await fetchCustomerContact(r.customer_id);
    setShareDoc({
      kind: "refund", id: r.id, customerId: r.customer_id || null, label: `Refund Receipt ${r.refund_number}`, ...contact,
      waMessage: waBusinessMessage({
        business, kind: "refund", heading: "Refund Receipt",
        meta: [`Reference: ${r.refund_number}`, `Date: ${fmtDate(r.created_at)}`],
        greeting: r.customer_name !== "Walk-in" ? r.customer_name : "",
        body: [`Amount Refunded: *${money(r.total)}*`, `Status: *Completed*`],
        closing: "Thank you for your patience.",
      }),
    });
  };

  const load = () => api.get("/sales").then((r) => setSales(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return sales;
    return sales.filter((inv) =>
      (inv.invoice_number || "").toLowerCase().includes(s) ||
      (inv.customer_name || "").toLowerCase().includes(s) ||
      (inv.created_by || "").toLowerCase().includes(s) ||
      (inv.sale_date || "").includes(s) ||
      String(inv.total || "").includes(s) ||
      (inv.items || []).some((it) => (it.name || "").toLowerCase().includes(s)));
  }, [sales, q]);

  const open = async (inv) => {
    try {
      const { data } = await api.get(`/sales/${inv.id}`);
      setDetail(data); setRefundMode(false); setRefundQty({}); setReason("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const ikey = (it) => `${it.product_id || ""}|${it.name}|${it.price}`;

  // Owner QA correction: item-level discount/net-price display is derived read-only from
  // the PERSISTED invoice line (it.price = original unit price, it.discount = per-unit
  // dollar discount already resolved at sale time) — never today's product price/discount
  // settings, and never recomputed into anything that changes a stored amount.
  const netUnitPrice = (it) => Math.round(((it.price || 0) - (it.discount || 0)) * 100) / 100;
  const hasItemDiscount = (it) => (it.discount || 0) > 0.004;

  // Per-item refund breakdown (display-only preview for the modal while a Cashier/Owner
  // is choosing a refund quantity). Mirrors the same pieces `refundPreview` already
  // aggregates below — it never feeds back into `submitRefund`, which still posts only
  // {product_id, name, price, discount, quantity} and lets the backend compute/approve
  // the authoritative refund the same way it always has.
  const itemRefundPreview = (it) => {
    const qn = refundQty[ikey(it)] || 0;
    if (qn <= 0) return null;
    const unit = netUnitPrice(it);
    const gross = Math.round(unit * qn * 100) / 100;
    const shareTotal = it.quantity ? (it.sale_discount_share || 0) * qn / it.quantity : 0;
    const saleDiscShare = Math.min(Math.round(shareTotal * 100) / 100, gross);
    const net = Math.max(0, Math.round((gross - saleDiscShare) * 100) / 100);
    const eff = detail?.subtotal ? detail.tax / detail.subtotal : 0;
    const tax = includeTax ? Math.round(net * eff * 100) / 100 : 0;
    return { qty: qn, unit, gross, saleDiscShare, tax, total: Math.round((net + tax) * 100) / 100 };
  };

  const defaultFormat = () => (business?.receipt_format === "thermal58" ? "thermal58" : "thermal80");

  const printRefund = (inv, refund, format = defaultFormat()) => {
    const cur = business?.currency || "JMD $";
    printDoc({
      business, receipt: format !== "pdf", format: format === "pdf" ? "standard" : format,
      docTitle: refund.kind === "void" ? "Void / Refund Receipt" : "Refund Receipt",
      docRef: refund.refund_number,
      meta: [
        ["Original", refund.original_invoice_number],
        ["Customer", refund.customer_name || inv.customer_name || "Walk-in"],
        ["Refunded By", refund.created_by || ""],
        ...(refund.approved_by ? [["Approved By", refund.approved_by]] : []),
        ["Reason", refund.reason || ""],
        ["Date", fmtDate(refund.created_at)],
      ],
      body: refundBody(refund, cur, { taxLabel: business?.tax_label || "Tax", walletLabel: walletName(business) }),
      accent: "#f43f5e", showBank: false, onDone: refocusDetail,
    });
  };

  useEffect(() => { setDiagModule("sales"); return () => setDiagModule(null); }, []);
  const reprint = (inv, refund = null, format = "pdf") => {
    const cid = beginInteraction("sales", "reprint"); requestAnimationFrame(() => endInteraction(cid, true));   // receipt build + window request; print stages follow under Printing
    if (refund) return printRefund(inv, refund, format);
    const cur = business?.currency || "JMD $";
    const src = inv;
    const body = itemsTable(inv.items, cur) + totalsBlock({
      items: inv.items,
      subtotal: inv.subtotal, tax: inv.tax, total: inv.total,
      taxLabel: business?.tax_label || "Tax",
      paid: inv.amount_paid || 0,
      balance: (inv.total || 0) - (inv.amount_paid || 0),
      tenders: inv.tenders, tendered: inv.amount_tendered, change: inv.change_given, paymentMethod: inv.payment_method, walletLabel: walletName(business),
      postedToCredit: inv.is_credit ? (inv.amount_on_account ?? ((inv.total || 0) - (inv.amount_paid || 0))) : null,
      originalSubtotal: inv.original_subtotal, discountAmount: inv.sale_discount_amount,
      deliveryFee: inv.delivery_fee, deliveryTax: inv.delivery_tax,
    }, cur) + pickupBlock(inv);
    printDoc({
      business, receipt: format !== "pdf", format: format === "pdf" ? "standard" : format, inline: format === "pdf",   // Full Page → same-tab frame + system dialog (global rule); thermal keeps its existing behaviour
      docTitle: inv.is_credit ? "Credit Invoice" : "Sales Receipt",
      docRef: inv.invoice_number,
      meta: [
        ["Customer", inv.customer_name || "Walk-in"],
        ["Cashier", inv.created_by || ""],
        ["Payment", /prepaid/i.test(inv.payment_method || "") ? walletName(business) : (inv.payment_method || (inv.is_credit ? "Credit" : "Cash"))],
        ["Date", fmtDate(inv.sale_date)],
      ],
      body, accent: "#10b981", showBank: !!inv.is_credit, onDone: refocusDetail,
    });
  };

  const startRefund = () => {
    const init = {};
    (detail.items || []).forEach((it) => { init[ikey(it)] = 0; });
    setRefundQty(init); setDisposition({}); setRefundDelivery(false); setIncludeTax(true); setReason(""); setTermBlocked(false); setRefundMode(true);
    api.get("/pos/register/me").then((r) => { const s = r.data; if (s && s.enforcement && s.status !== "not_applicable" && !["ok", "legacy"].includes(s.status)) setTermBlocked(true); }).catch(() => {});
  };

  const refundPreview = useMemo(() => {
    if (!detail) return { sub: 0, saleDisc: 0, net: 0, tax: 0, dFee: 0, dTax: 0, total: 0 };
    const eff = detail.subtotal ? detail.tax / detail.subtotal : 0;
    let sub = 0, saleDisc = 0, allReturned = true;
    (detail.items || []).forEach((it) => {
      const qn = refundQty[ikey(it)] || 0;
      sub += (it.price - (it.discount || 0)) * qn;
      saleDisc += it.quantity ? (it.sale_discount_share || 0) * qn / it.quantity : 0;
      if ((it.refundable_qty || 0) - qn > 0.0001) allReturned = false;
    });
    const discLeft = Math.max(0, (detail.sale_discount_amount || 0) - (detail.sale_discount_refunded || 0));
    saleDisc = Math.min(Math.round(saleDisc * 100) / 100, discLeft);
    if (allReturned && (detail.sale_discount_amount || 0) > 0 && sub > 0) saleDisc = discLeft;
    const net = Math.max(0, sub - saleDisc);
    const tax = includeTax ? net * eff : 0;
    const dFee = refundDelivery ? (detail.delivery_fee || 0) : 0;
    const dTax = refundDelivery && includeTax ? (detail.delivery_tax || 0) : 0;
    return { sub, saleDisc, net, tax, dFee, dTax, total: net + tax + dFee + dTax };
  }, [detail, refundQty, refundDelivery, includeTax]);
  const refundTotalPreview = refundPreview.total;

  // Owner QA correction — display-only reconciliation ladder for the detail view: gross
  // items total (before any discount) down to the persisted net merchandise subtotal, so
  // item discounts + the whole-sale discount are never implied to be subtracted twice.
  // `original_subtotal` is read as-is from the persisted invoice (never recomputed from
  // today's product prices); a historical sale that predates the field simply has no item
  // discount to show (falls back to the gross total, matching its actual persisted state).
  const detailLadder = useMemo(() => {
    if (!detail) return null;
    const itemsGross = Math.round((detail.items || []).reduce((s, it) => s + (it.price || 0) * it.quantity, 0) * 100) / 100;
    const postItemDiscSubtotal = detail.original_subtotal != null ? detail.original_subtotal : itemsGross;
    const itemDiscTotal = Math.max(0, Math.round((itemsGross - postItemDiscSubtotal) * 100) / 100);
    return { itemsGross, postItemDiscSubtotal, itemDiscTotal };
  }, [detail]);

  const submitRefund = async (pin) => {
    if (refundInFlight.current) return;
    refundInFlight.current = true; setRefundBusy(true);
    try { await _submitRefund(pin); } finally { refundInFlight.current = false; setRefundBusy(false); }
  };

  const _submitRefund = async (pin) => {
    const items = (detail.items || [])
      .map((it) => ({ product_id: it.product_id, name: it.name, price: it.price, discount: it.discount || 0, quantity: refundQty[ikey(it)] || 0,
        ...(business?.is_fashion && it.product_id ? { disposition: disposition[ikey(it)]?.kind || "restock", disposition_reason: disposition[ikey(it)]?.reason || "" } : {}) }))
      .filter((it) => it.quantity > 0);
    try {
      if (!refundIdemKey.current) refundIdemKey.current = newIdemKey();   // reused across the PIN approval retry
      const { data } = await api.post("/refunds", { original_invoice_id: detail.id, items, reason, manager_pin: pin, refund_delivery: refundDelivery, include_tax: includeTax }, idemHeaders(refundIdemKey.current));
      refundIdemKey.current = null;
      toast.success(`Refund ${data.refund_number} approved by ${data.approved_by}`);
      setPinOpen(false); setRefundMode(false);
      await open({ id: detail.id });   // refresh detail
      load();
      printRefund(detail, data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) {
        if (pin) throw new Error(d.message || "Manager approval required");
        setPinOpen(true);   // cashier over limit — prompt for Manager/Owner PIN
        return;
      }
      setPinOpen(false);
      if (d?.code === "register_required") { setTermBlocked(true); toast.error(TERMINAL_MSG); }
      else toast.error(apiError(d));
      if (pin) throw new Error("Refund failed");
    }
  };

  const anyQty = Object.values(refundQty).some((v) => v > 0) || refundDelivery;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><Receipt className="w-7 h-7" /> Sales History &amp; Receipts</h1>
          <p className="text-muted-foreground">Search completed transactions, reprint receipts and process returns/refunds.</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sales-search"
          placeholder="Receipt #, date, cashier, customer, item or amount…" className="pl-9" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {filtered.map((inv) => (
            <button key={inv.id} onClick={() => open(inv)} data-testid={`sale-row-${inv.invoice_number}`}
              className="w-full text-left px-4 py-3 hover:bg-secondary/50 flex flex-wrap items-center gap-3">
              <span className="mono font-semibold w-28">{inv.invoice_number}</span>
              <span className="text-sm text-muted-foreground w-24">{fmtDate(inv.sale_date)}</span>
              <span className="text-sm flex-1 min-w-[120px]">{inv.customer_name}</span>
              <Badge variant="outline" className="text-[10px]">{inv.is_credit ? "Credit" : /prepaid/i.test(inv.payment_method || "") ? walletName(business) : (inv.payment_method || "Cash")}</Badge>
              {inv.pickup_reference && <Badge className={`text-[10px] ${fulfillmentBadge(inv.fulfillment_status)}`} data-testid={`fulfillment-badge-${inv.invoice_number}`}>{inv.pickup_reference} · {inv.fulfillment_label}</Badge>}
              {refundBadge(inv, "text-[10px]")}
              <span className="mono font-bold w-32 text-right">{money(inv.total)}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-10">No matching transactions.</p>}
        </div>
      </Card>

      {/* Transaction detail */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setRefundMode(false); } }}>
        {/* Refund mode (any invoice, 2 lines or 20+) widens into a desktop two-pane layout:
            ~65% product list (own bounded scroll) / ~35% controls+summary+actions, so a long
            return never pushes the reason/total/Approve button offscreen. Plain view mode keeps
            its original single-column width/behavior untouched. Mobile stays a single stacked,
            scrollable column either way — only the desktop grid split is new. */}
        <DialogContent className={refundMode ? "max-w-4xl max-h-[90vh] p-0 overflow-hidden flex flex-col" : "max-w-lg max-h-[90vh] overflow-y-auto"} data-testid="sale-detail-modal">
          {detail && (
            <>
              <div className={refundMode ? "px-6 pt-6 shrink-0" : "contents"}>
                <DialogHeader>
                  <DialogTitle className="font-head flex items-center justify-between">
                    <span className="flex items-center gap-2">{detail.invoice_number}{refundBadge(detail, "text-[10px]")}</span>
                    <Badge variant="outline">{detail.is_credit ? "Credit" : /prepaid/i.test(detail.payment_method || "") ? walletName(business) : (detail.payment_method || "Cash")}</Badge>
                  </DialogTitle>
                  <DialogDescription>
                    {detail.customer_name} · {fmtDate(detail.sale_date)} · Cashier {detail.created_by}
                  </DialogDescription>
                </DialogHeader>
                {detail.pickup_reference && (
                  <div className={`rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 flex items-center justify-between text-sm${refundMode ? " mt-3" : ""}`} data-testid="detail-pickup-info">
                    <span>Pickup <span className="mono font-bold">{detail.pickup_reference}</span> · Payment {detail.payment_status}</span>
                    <Badge className={fulfillmentBadge(detail.fulfillment_status)}>{detail.fulfillment_label}</Badge>
                  </div>
                )}
              </div>

              <div className={refundMode
                ? "flex-1 min-h-0 overflow-y-auto md:overflow-hidden px-6 pb-6 pt-3 flex flex-col md:grid md:grid-cols-[1.65fr_1fr] gap-4 md:gap-5"
                : "contents"}>
              <div className={refundMode ? "min-h-0 flex flex-col shrink-0 md:overflow-y-auto md:pr-1" : "contents"}>
              <div className="rounded-xl border border-border divide-y divide-border">
                {detail.items.map((it, i) => {
                  const disc = hasItemDiscount(it);
                  const unit = netUnitPrice(it);
                  const refPrev = refundMode ? itemRefundPreview(it) : null;
                  return (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`detail-item-${i}`}>
                    <div>
                      <p className="font-medium">{it.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {it.quantity} × {money(it.price)}{it.refunded_qty > 0 && <span className="text-rose-500"> · {it.refunded_qty} refunded</span>}
                      </p>
                      {disc && (
                        <p className="text-xs text-emerald-600" data-testid={`detail-item-discount-${i}`}>
                          {it.discount_type === "override"
                            ? <>Set Price {money(it.regular_price ?? it.price)} → {money(it.negotiated_price ?? unit)} each</>
                            : <>{it.discount_effective_pct != null ? `${it.discount_effective_pct}% ` : ""}item discount −{money(it.discount)} each → {money(unit)} each</>}
                        </p>
                      )}
                    </div>
                    {refundMode ? (
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">max {it.refundable_qty}</span>
                          <Input type="number" min="0" max={it.refundable_qty} value={refundQty[ikey(it)] ?? 0}
                            onChange={(e) => {
                              let v = Math.max(0, Math.min(it.refundable_qty, parseFloat(e.target.value || 0)));
                              setRefundQty({ ...refundQty, [ikey(it)]: v });
                            }}
                            className="w-20 h-8" data-testid={`refund-qty-${i}`} disabled={it.refundable_qty <= 0} />
                        </div>
                        {refPrev && (
                          <div className="text-[11px] text-muted-foreground text-right leading-tight" data-testid={`refund-item-breakdown-${i}`}>
                            <p>{refPrev.qty} × {money(it.price)}{disc && <> −{money(it.discount)} disc. = {money(refPrev.unit)} ea</>}</p>
                            {refPrev.saleDiscShare > 0.004 && <p className="text-emerald-600">−{money(refPrev.saleDiscShare)} sale discount share</p>}
                            {includeTax && <p>+{money(refPrev.tax)} {business?.tax_label || "tax"} refunded</p>}
                            <p className="font-semibold text-foreground">{money(refPrev.total)} refund</p>
                          </div>
                        )}
                        {business?.is_fashion && it.product_id && (refundQty[ikey(it)] || 0) > 0 && (
                          <div className="flex items-center gap-1.5" data-testid={`refund-disposition-${i}`}>
                            <select className="h-7 rounded-md border border-border bg-background px-1.5 text-xs" value={disposition[ikey(it)]?.kind || "restock"}
                              onChange={(e) => setDisposition({ ...disposition, [ikey(it)]: { ...(disposition[ikey(it)] || {}), kind: e.target.value } })} data-testid={`refund-disposition-select-${i}`}>
                              <option value="restock">Return to stock</option>
                              <option value="hold">Hold for review</option>
                            </select>
                            <Input className="h-7 w-36 text-xs" placeholder={disposition[ikey(it)]?.kind === "hold" ? "Why hold? (optional)" : "Restock reason"} value={disposition[ikey(it)]?.reason || ""}
                              onChange={(e) => setDisposition({ ...disposition, [ikey(it)]: { ...(disposition[ikey(it)] || { kind: "restock" }), reason: e.target.value } })} data-testid={`refund-disposition-reason-${i}`} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="mono" data-testid={`detail-item-total-${i}`}>{money(unit * it.quantity)}</span>
                    )}
                  </div>
                  );
                })}
                {detail.delivery_fee > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 text-sm bg-sky-500/5" data-testid="detail-delivery-line">
                    <div>
                      <p className="font-medium">Delivery charge <span className="text-[10px] uppercase tracking-wider text-sky-600 ml-1">non-inventory</span></p>
                      <p className="text-xs text-muted-foreground">
                        {money(detail.delivery_fee)}{detail.delivery_tax > 0 ? ` + ${business?.tax_label || "Tax"} ${money(detail.delivery_tax)}` : " · no tax"}
                        {detail.delivery_status === "delivered" && " · Delivered"}
                        {detail.delivery_status === "awaiting" && " · Awaiting delivery"}
                        {detail.delivery_refunded > 0 && <span className="text-rose-500"> · refunded {money(detail.delivery_refunded)}</span>}
                      </p>
                    </div>
                    {refundMode ? (
                      detail.delivery_refundable ? (
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={refundDelivery} onChange={(e) => setRefundDelivery(e.target.checked)} data-testid="refund-delivery-check" />
                          Refund delivery
                        </label>
                      ) : <Badge variant="outline" className="text-rose-500" data-testid="delivery-refunded-badge">Refunded</Badge>
                    ) : (
                      <span className="mono">{money((detail.delivery_fee || 0) + (detail.delivery_tax || 0))}</span>
                    )}
                  </div>
                )}
              </div>
              </div>

              <div className={refundMode ? "min-h-0 flex flex-col gap-3 shrink-0 md:overflow-y-auto md:pl-1 md:border-l md:border-border" : "contents"}>
              {detailLadder && (detailLadder.itemDiscTotal > 0.004 || detail.sale_discount_amount > 0) && (
                <div className="flex justify-between text-sm px-1" data-testid="detail-items-gross">
                  <span className="text-muted-foreground">Items total (before discounts)</span><span className="mono">{money(detailLadder.itemsGross)}</span>
                </div>
              )}
              {detailLadder && detailLadder.itemDiscTotal > 0.004 && (
                <div className="flex justify-between text-sm px-1 -mt-2 text-emerald-600" data-testid="detail-item-discounts-total">
                  <span>Item discounts</span><span className="mono">-{money(detailLadder.itemDiscTotal)}</span>
                </div>
              )}
              {detailLadder && detailLadder.itemDiscTotal > 0.004 && detail.sale_discount_amount > 0 && (
                <div className="flex justify-between text-sm px-1 -mt-2" data-testid="detail-subtotal-after-item-discounts">
                  <span className="text-muted-foreground">Subtotal after item discounts</span><span className="mono">{money(detailLadder.postItemDiscSubtotal)}</span>
                </div>
              )}
              {detail.sale_discount_amount > 0 && (
                <div className="flex justify-between text-sm px-1 -mt-2 text-emerald-600" data-testid="detail-sale-discount">
                  <span>Whole-sale discount</span><span className="mono">-{money(detail.sale_discount_amount)}</span>
                </div>
              )}
              <div className={`flex justify-between text-sm px-1 ${detailLadder && (detailLadder.itemDiscTotal > 0.004 || detail.sale_discount_amount > 0) ? "-mt-2" : ""}`} data-testid="detail-subtotal">
                <span className="text-muted-foreground">{detailLadder && (detailLadder.itemDiscTotal > 0.004 || detail.sale_discount_amount > 0) ? "Subtotal (after discounts)" : "Subtotal"}</span><span className="mono">{money(detail.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm px-1 -mt-2">
                <span className="text-muted-foreground">{business?.tax_label || "Tax"}</span><span className="mono">{money(detail.tax)}</span>
              </div>
              {detail.delivery_fee > 0 && (
                <div className="flex justify-between text-sm px-1 -mt-2" data-testid="detail-delivery-total">
                  <span className="text-muted-foreground">Delivery{detail.delivery_tax > 0 ? ` + ${business?.tax_label || "Tax"}` : ""}</span>
                  <span className="mono">{money((detail.delivery_fee || 0) + (detail.delivery_tax || 0))}</span>
                </div>
              )}
              <div className="flex justify-between font-bold px-1 -mt-2">
                <span>Total</span><span className="mono">{money(detail.total)}</span>
              </div>
              {detail.total_refunded > 0 && (
                <div className="flex justify-between text-sm px-1 -mt-1 text-rose-500">
                  <span>Total refunded</span><span className="mono">-{money(detail.total_refunded)}</span>
                </div>
              )}

              {detail.refunds?.length > 0 && !refundMode && (
                <div className="rounded-xl bg-rose-500/5 border border-rose-500/20 p-3 space-y-1" data-testid="refund-history">
                  <p className="text-xs font-semibold uppercase tracking-wider text-rose-500">Refund History</p>
                  {detail.refunds.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs" data-testid={`refund-row-${r.refund_number}`}>
                      <span className="mono">{r.refund_number}</span>
                      <span className="text-muted-foreground">{r.reason}</span>
                      <span>appr: {r.approved_by}</span>
                      <span className="mono font-semibold">-{money(r.total)}</span>
                      <span className="flex items-center gap-1">
                        <Printer className="w-3 h-3 text-muted-foreground" />
                        <button type="button" className="px-1.5 py-0.5 rounded border border-border hover:bg-secondary" onClick={() => printRefund(detail, r, "thermal80")} data-testid={`refund-print-80-${r.refund_number}`}>80mm</button>
                        <button type="button" className="px-1.5 py-0.5 rounded border border-border hover:bg-secondary" onClick={() => printRefund(detail, r, "thermal58")} data-testid={`refund-print-58-${r.refund_number}`}>58mm</button>
                        <button type="button" className="px-1.5 py-0.5 rounded border border-border hover:bg-secondary text-muted-foreground" onClick={() => printRefund(detail, r, "pdf")} data-testid={`refund-print-pdf-${r.refund_number}`}>PDF</button>
                        <button type="button" className="px-1.5 py-0.5 rounded border border-border hover:bg-secondary text-emerald-600 flex items-center gap-1" onClick={() => openShareRefund(r)} data-testid={`refund-share-${r.refund_number}`} title="Digital Share"><Share2 className="w-3 h-3" /> Share</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {refundMode && termBlocked ? (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-2" data-testid="refund-terminal-required">
                  <p className="text-sm font-semibold">{TERMINAL_MSG}</p>
                  <p className="text-xs text-muted-foreground">You can still view sales and reports from this device.</p>
                  <Button variant="outline" size="sm" onClick={() => setRefundMode(false)} data-testid="refund-terminal-back">Back to sale</Button>
                </div>
              ) : refundMode ? (
                <>
                  <div>
                    <Label className="text-xs">Refund reason (required)</Label>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Damaged item" data-testid="refund-reason" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">Include {business?.tax_label || "tax"} in refund</p>
                      <p className="text-xs text-muted-foreground">Uses the tax charged on the original sale — never today's rate.</p>
                    </div>
                    <input type="checkbox" checked={includeTax} onChange={(e) => setIncludeTax(e.target.checked)} data-testid="refund-include-tax" />
                  </div>
                  <div className="rounded-lg bg-secondary/50 px-3 py-2 space-y-1 text-sm" data-testid="refund-summary">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Returning</p>
                    <div className="flex justify-between"><span>Products</span><span className="mono">{money(refundPreview.sub)}</span></div>
                    {refundPreview.saleDisc > 0 && <div className="flex justify-between text-emerald-600"><span>Share of original sale discount</span><span className="mono" data-testid="refund-sale-discount-preview">-{money(refundPreview.saleDisc)}</span></div>}
                    {refundDelivery && <div className="flex justify-between"><span>Delivery</span><span className="mono" data-testid="refund-delivery-preview">{money(refundPreview.dFee)}</span></div>}
                    <div className="flex justify-between"><span>{business?.tax_label || "Tax"} {includeTax ? "(included)" : "(excluded)"}</span><span className="mono">{money(refundPreview.tax + refundPreview.dTax)}</span></div>
                    <div className="flex justify-between font-bold border-t border-border pt-1">
                      <span>Refund total</span>
                      <span className="mono text-rose-500" data-testid="refund-total-preview">{money(refundTotalPreview)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Remaining on sale after this refund</span>
                      <span className="mono" data-testid="refund-remaining-preview">{money(Math.max(0, (detail.total || 0) - (detail.total_refunded || 0) - refundTotalPreview))}</span>
                    </div>
                  </div>
                  {refundDelivery && detail.delivery_status === "delivered" && (
                    <p className="text-xs text-amber-600" data-testid="refund-delivery-delivered-note">This delivery was already completed. Owners/Managers confirm by approving; cashiers will be asked for a Manager/Owner PIN.</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => setRefundMode(false)} data-testid="refund-cancel">Cancel</Button>
                    <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={refundBusy || !anyQty || !reason.trim()}
                      onClick={() => submitRefund(null)} data-testid="refund-approve">
                      <ShieldCheck className="w-4 h-4 mr-1.5" /> {refundBusy ? "Processing…" : <>Approve &amp; Refund</>}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Owners &amp; Managers approve directly. Cashiers above the refund limit will be prompted for a Manager/Owner PIN.</p>
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Button ref={reprintRef} variant="outline" onClick={() => reprint(detail, null, defaultFormat())} data-testid="reprint-btn"><Printer className="w-4 h-4 mr-1.5" /> Reprint 80mm</Button>
                  <Button variant="outline" size="sm" className="h-10" onClick={() => reprint(detail, null, "thermal58")} data-testid="reprint-58-btn">58mm</Button>
                  <Button variant="outline" size="sm" className="h-10 text-muted-foreground" onClick={() => reprint(detail, null, "pdf")} data-testid="reprint-pdf-btn">PDF</Button>
                  <Button variant="outline" className="text-emerald-600 border-emerald-500/40" onClick={() => openShareSale(detail)} data-testid="sale-share-btn"><Share2 className="w-4 h-4 mr-1.5" /> Digital Share</Button>
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white ml-auto" onClick={startRefund}
                    disabled={!detail.items.some((it) => it.refundable_qty > 0) && !detail.delivery_refundable} data-testid="start-refund-btn">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Return / Refund
                  </Button>
                </div>
              )}
              </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)}
        title="Refund Approval Required"
        reason="A Manager or Owner PIN is required to authorize this refund."
        onSubmit={submitRefund} />

      <DocumentShareDialog open={!!shareDoc} onOpenChange={(o) => !o && setShareDoc(null)}
        title={shareDoc?.label || "Share Receipt"} docLabel={shareDoc?.label} waMessage={shareDoc?.waMessage}
        customerId={shareDoc?.customerId} customerEmail={shareDoc?.email} customerPhone={shareDoc?.phone}
        fileName={`${shareDoc?.label || "document"}.pdf`}
        getPdfBlob={() => api.get(`/${shareBase()}/${shareDoc.id}/receipt.pdf`, { responseType: "blob" }).then((r) => r.data)}
        onEmail={(idem) => api.post(`/${shareBase()}/${shareDoc.id}/receipt/email`, { idempotency_key: idem })}
        onWhatsAppShared={(method) => api.post(`/${shareBase()}/${shareDoc.id}/receipt/whatsapp-shared`, { method }).catch(() => {})} />
    </div>
  );
}
